import * as XLSX from 'xlsx';
import {
  sanitiseStaffId, checkStaffId,
  sanitiseEpf, checkEpf,
  normaliseNic, checkNic,
  checkDateOfBirth, checkDateOfJoining,
  sanitisePhone, checkPhone, checkMobile,
  checkEmail,
  sanitisePostal, checkPostal,
  sanitiseDecimal, checkDecimal,
  parseExperience, formatExperience, checkExperience,
  toIso,
} from './employeeValidation';
import { contactTypeFor } from './contactType';
import { normaliseTin } from './tinUnique';
import { isContractCategory } from './employeeUtils';


const fmt = (v) => (v === null || v === undefined ? '' : String(v).trim());

/* ── Choice lists, matching the Add form exactly ──────────────────────────── */

export const TITLE_CHOICES = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Rev'];

export const GENDER_CHOICES = [
  { code: 'M', label: 'Male' },
  { code: 'F', label: 'Female' },
];

export const MARITAL_CHOICES = [
  { code: 'S', label: 'Single' },
  { code: 'M', label: 'Married' },
  { code: 'D', label: 'Divorced' },
  { code: 'W', label: 'Widowed' },
];

export const RELIGION_CHOICES = [
  { code: 'B', label: 'Buddhist' },
  { code: 'C', label: 'Christian' },
  { code: 'H', label: 'Hindu' },
  { code: 'I', label: 'Islam' },
  { code: 'O', label: 'Other' },
];

export const BLOOD_CHOICES = [
  { code: '1', label: 'A+' }, { code: '2', label: 'A-' },
  { code: '3', label: 'B+' }, { code: '4', label: 'B-' },
  { code: '5', label: 'AB+' }, { code: '6', label: 'AB-' },
  { code: '7', label: 'O+' }, { code: '8', label: 'O-' },
];

export const ACCOUNT_TYPE_CHOICES = ['Savings', 'Current'];

/** Accepts either the label ("Male") or the stored code ("M"). */
function codeFromChoices(value, choices) {
  const v = fmt(value).toLowerCase();
  if (!v) return '';
  const hit = choices.find(
    (c) => c.label.toLowerCase() === v || c.code.toLowerCase() === v
  );
  return hit ? hit.code : '';
}

const choiceLabels = (choices) => choices.map((c) => c.label).join(' / ');


export const COLUMNS = [
  // ── Identification ──
  { key: 'serviceNo',  header: 'Staff ID',        required: true,  width: 14, example: 'EMP1001',
    note: 'Max 10 characters. Must not already exist.' },
  { key: 'epfNo',      header: 'EPF No',          required: true,  width: 12, example: 'E1001',
    note: 'Max 5 characters. ETF is saved with the same value.' },
  { key: 'tinNo',      header: 'TIN No',          required: false, width: 14, example: '123456789',
        note: 'Taxpayer Identification Number. Optional. Numbers only. Must not already be used by another employee.' },
  { key: 'nicNo',      header: 'NIC No',          required: true,  width: 18, example: '199012345678',
    note: '12 digits, or 9 digits + V (e.g. 901234567V).' },
  { key: 'passportNo', header: 'Passport No',     required: false, width: 14, example: '' },

  // ── Name ──
  { key: 'title',      header: 'Title',           required: false, width: 10, example: 'Mr',
    note: TITLE_CHOICES.join(' / ') },
  { key: 'firstName',  header: 'First Name',      required: true,  width: 18, example: 'Kasun' },
  { key: 'middleName', header: 'Middle Name',     required: false, width: 18, example: '' },
  { key: 'lastName',   header: 'Last Name',       required: true,  width: 18, example: 'Perera' },
  { key: 'initials',   header: 'Initials',        required: true,  width: 12, example: 'K.P.',
    note: 'Required. Also used as the bank account holder name.' },
  { key: 'reportName', header: 'Report Name',     required: false, width: 24, example: '',
    note: 'Leave blank to build it from first + middle + last name.' },

  // ── Personal ──
  { key: 'gender',        header: 'Gender',         required: false, width: 10, example: 'Male',
    note: choiceLabels(GENDER_CHOICES) },
  { key: 'dateOfBirth',   header: 'Date of Birth',  required: true,  width: 14, example: '1990-05-14',
    note: 'yyyy-mm-dd. Must be at least 18 years ago.' },
  { key: 'maritalStatus', header: 'Marital Status', required: false, width: 14, example: 'Single',
    note: choiceLabels(MARITAL_CHOICES) },
  { key: 'religion',      header: 'Religion',       required: false, width: 12, example: 'Buddhist',
    note: choiceLabels(RELIGION_CHOICES) },
  { key: 'bloodGroup',    header: 'Blood Group',    required: false, width: 12, example: 'O+',
    note: choiceLabels(BLOOD_CHOICES) },

  // ── Contact ──
  { key: 'mobileNo',      header: 'Mobile No',      required: true,  width: 14, example: '0771234567',
    note: 'Numbers only, exactly 10.' },
  { key: 'homeNo',        header: 'Home Phone',     required: false, width: 14, example: '' },
  { key: 'officeNo',      header: 'Office Phone',   required: false, width: 14, example: '' },
  { key: 'personalEmail', header: 'Personal Email', required: false, width: 26, example: 'kasun@gmail.com',
    note: 'Must contain @.' },
  { key: 'companyEmail',  header: 'Company Email',  required: false, width: 26, example: '' },

  // ── Employment ──
  { key: 'designation', header: 'Designation', required: true,  width: 24, example: 'Senior Managers',
    lookup: 'designations', note: 'Must match a Designation name exactly — see the Reference Lists sheet.' },
  { key: 'department',  header: 'Department',  required: true,  width: 24, example: 'Design',
    lookup: 'departments', note: 'See the Reference Lists sheet.' },
  { key: 'location',    header: 'Location',    required: true,  width: 20, example: 'Head Office',
    lookup: 'locations', note: 'See the Reference Lists sheet.' },
  { key: 'grade',       header: 'Grade',       required: true,  width: 20, example: 'Grade A',
    lookup: 'grades', note: 'See the Reference Lists sheet.' },
  { key: 'category',    header: 'Employment Category', required: true,  width: 20, example: 'Executive',
    lookup: 'categories', note: 'See the Reference Lists sheet.' },
  { key: 'shift',       header: 'Shift',       required: true,  width: 18, example: 'Day Shift',
    lookup: 'shifts', note: 'See the Reference Lists sheet.' },

  { key: 'dateOfJoining',    header: 'Date of Joining',   required: true,  width: 16, example: '2024-01-15',
    note: 'yyyy-mm-dd. Cannot be a future date.' },

  // Career dates — for every category EXCEPT Contract. Contract rows leave
  // these four blank (they are ignored) and fill Termination Date instead.
  { key: 'recruitmentDate', header: 'Recruitment Date', required: false, width: 16, example: '2024-01-15',
    note: 'yyyy-mm-dd. Cannot be a future date. Leave blank for Contract employees.' },
  { key: 'promotedDate',    header: 'Promoted Date',    required: false, width: 16, example: '',
    note: 'yyyy-mm-dd. Leave blank for Contract employees.' },
  { key: 'permanentDate',   header: 'Permanent Date',   required: false, width: 16, example: '',
    note: 'yyyy-mm-dd. Leave blank for Contract employees.' },
  { key: 'retirementDate',  header: 'Retirement Date',  required: false, width: 16, example: '',
    note: 'yyyy-mm-dd. Leave blank for Contract employees.' },
  { key: 'terminationDate', header: 'Termination Date', required: false, width: 16, example: '',
    note: 'yyyy-mm-dd. REQUIRED when Employment Category is Contract. Leave blank for every other category.' },
  { key: 'noticePeriodDays', header: 'Notice Period Days', required: false, width: 16, example: '30' },
  { key: 'otRate',           header: 'OT Rate',            required: false, width: 12, example: '' },
  { key: 'totalExperience',  header: 'Total Experience',   required: false, width: 16, example: '2Y6M',
    note: 'Years and months, e.g. 2Y6M, 3Y or 6M.' },
  { key: 'costCenter',       header: 'Cost Center',        required: false, width: 18, example: '' },
  { key: 'reportingEmployee', header: 'Reporting Employee', required: false, width: 26, example: '',
    lookup: 'employees',
    note: 'The manager\u2019s Staff ID or name \u2014 see the Reference Lists sheet. Must be an employee that already exists.' },
  { key: 'group',            header: 'Group',              required: false, width: 16, example: '' },

  /* ── Permanent address ── */
  { key: 'permanentAddressLine1', header: 'Permanent Address Line 1', required: false, width: 24, example: 'No. 25',
    aliases: ['Address Line 1'] },
  { key: 'permanentAddressLine2', header: 'Permanent Address Line 2', required: false, width: 24, example: 'Galle Road',
    aliases: ['Address Line 2'] },
  { key: 'permanentAddressLine3', header: 'Permanent Address Line 3', required: false, width: 24, example: '',
    aliases: ['Address Line 3'] },
  { key: 'permanentAddressCity',  header: 'Permanent City',           required: false, width: 18, example: 'Colombo',
    aliases: ['City'] },
  { key: 'permanentAddressPostal', header: 'Permanent Postal Code',   required: false, width: 14, example: '10350',
    aliases: ['Postal Code'], note: 'Exactly 5 numbers.' },
  { key: 'distance',      header: 'Distance (km)',  required: false, width: 13, example: '12.5',
    note: 'Distance from this address to the workplace. Decimals allowed (e.g. 12.5).' },

  /* ── Region ──  */
  { key: 'province',      header: 'Province',       required: false, width: 18, example: '',
    lookup: 'provinces', note: 'Province name, exactly as in the Reference Lists sheet.' },
  { key: 'district',      header: 'District',       required: false, width: 18, example: '',
    lookup: 'districts', note: 'Must be a district of the Province in the same row.' },
  { key: 'electorate',    header: 'Electorate',     required: false, width: 20, example: '',
    lookup: 'electorates', note: 'Must belong to the Province and District in the same row.' },
  { key: 'policeStation', header: 'Police Station', required: false, width: 22, example: '',
    lookup: 'policeStations', note: 'Must belong to the Electorate in the same row.' },

  /* ── Current address ── */
  { key: 'currentAddressLine1', header: 'Current Address Line 1', required: false, width: 24, example: '',
    note: 'Leave the current-address columns blank if it is the same as the permanent address.' },
  { key: 'currentAddressLine2', header: 'Current Address Line 2', required: false, width: 24, example: '' },
  { key: 'currentAddressLine3', header: 'Current Address Line 3', required: false, width: 24, example: '' },
  { key: 'currentAddressCity',  header: 'Current City',           required: false, width: 18, example: '' },
  { key: 'currentAddressPostal', header: 'Current Postal Code',   required: false, width: 14, example: '',
    note: 'Exactly 5 numbers.' },

  // ── Bank ──
  { key: 'bankName',      header: 'Bank',           required: false, width: 22, example: 'Sampath Bank',
    lookup: 'banks', note: 'Only approved banks can be used — see the Reference Lists sheet.' },
  { key: 'branchName',    header: 'Bank Branch',    required: false, width: 22, example: 'Kollupitiya',
    lookup: 'branches', note: 'Must belong to the bank above.' },
  { key: 'accountNumber', header: 'Account Number', required: false, width: 22, example: '100234567890' },
  { key: 'accountType',   header: 'Account Type',   required: false, width: 14, example: 'Savings',
    note: ACCOUNT_TYPE_CHOICES.join(' / ') + '. Blank means Savings.' },

  // ── Contact (same as the form's Contact & Emergency tab) ──
  { key: 'altContactName',         header: 'Contact Name',             required: false, width: 22, example: 'Nimal Perera',
    aliases: ['Emergency Contact Name'] },
  { key: 'altContactRelationship', header: 'Contact Relationship',     required: false, width: 18, example: 'Father',
    aliases: ['Emergency Relationship'] },
  { key: 'altContactNumber',       header: 'Contact No',               required: false, width: 18, example: '0712345678',
    aliases: ['Emergency Contact No'], note: 'Numbers only, max 10.' },
  { key: 'altContactAddress',      header: 'Contact Address',          required: false, width: 24, example: '',
    aliases: ['Emergency Address'] },
  { key: 'contactIsEmergency',     header: 'Emergency Contact',        required: false, width: 16, example: 'Yes',
    aliases: ['Set as Emergency Contact', 'Is Emergency'],
    note: 'Yes / No. Yes saves this contact as the emergency contact; blank means No.' },

  // ── Misc ──
  { key: 'remarks', header: 'Remarks', required: false, width: 24, example: '' },
];

export const HEADERS = COLUMNS.map((c) => c.header);

const SECOND_EXAMPLE = {
  serviceNo: 'EMP1002', epfNo: 'E1002', tinNo: '987654321', nicNo: '921234567V', firstName: 'Nadeesha',
  lastName: 'Silva', initials: 'N.S.', title: 'Ms', gender: 'Female',
  dateOfBirth: '1992-11-03', maritalStatus: 'Married', religion: 'Christian',
  bloodGroup: 'A+', distance: '8.25', mobileNo: '0719876543', personalEmail: 'nadeesha@gmail.com',
  designation: 'Senior Managers', department: 'Design', location: 'Head Office', grade: 'Grade A',
  category: 'Executive', shift: 'Day Shift', dateOfJoining: '2023-06-01', noticePeriodDays: '30',
  totalExperience: '5Y', permanentAddressLine1: 'No. 8', permanentAddressCity: 'Kandy',
  permanentAddressPostal: '20000', province: 'Central', district: 'Kandy',
  currentAddressLine1: 'No. 14/2', currentAddressLine2: 'Temple Road',
  currentAddressCity: 'Colombo', currentAddressPostal: '10250', bankName: 'Sampath Bank', branchName: 'Kollupitiya',
  accountNumber: '100987654321', accountType: 'Current',
  altContactName: 'Sunil Silva', altContactRelationship: 'Father', altContactNumber: '0771112223',
  contactIsEmergency: 'No',
};

/* ── Template ────────────────────────────────────────────────────────────── */


export function buildTemplateWorkbook(lookups = {}) {
  const wb = XLSX.utils.book_new();

  /* Sheet 1 — the one people type into. */
  const exampleRow = COLUMNS.map((c) => c.example ?? '');
  const secondRow = COLUMNS.map((c) => SECOND_EXAMPLE[c.key] ?? '');
  const ws = XLSX.utils.aoa_to_sheet([HEADERS, exampleRow, secondRow]);
  ws['!cols'] = COLUMNS.map((c) => ({ wch: c.width || 16 }));
  XLSX.utils.book_append_sheet(wb, ws, 'Employees');

  /* Sheet 2 — what every column means. */
  const guide = [
    ['Column', 'Required', 'Format / allowed values'],
    ...COLUMNS.map((c) => [c.header, c.required ? 'Yes' : 'No', c.note || '']),
    [],
    ['How to use this file', '', ''],
    ['1', 'Delete the two example rows before importing.', ''],
    ['2', 'One employee per row. Do not rename, reorder or remove the header row.', ''],
    ['3', 'Dates must be typed as yyyy-mm-dd (e.g. 2024-01-15).', ''],
    ['4', 'Name columns (Department, Designation, Bank…) must match the Reference Lists sheet exactly.', ''],
    ['5', 'Every imported employee is created unapproved and must be approved in the Approvals tab.', ''],
    ['6', 'Contract employees (Employment Category = Contract): Termination Date is mandatory. Do not fill Recruitment Date, Promoted Date, Permanent Date or Retirement Date — they are not used for Contract employees.', ''],
    ['7', 'All other categories: leave Termination Date blank. Recruitment, Promoted, Permanent and Retirement Date are optional.', ''],
  ];
  const wsGuide = XLSX.utils.aoa_to_sheet(guide);
  wsGuide['!cols'] = [{ wch: 26 }, { wch: 10 }, { wch: 80 }];
  XLSX.utils.book_append_sheet(wb, wsGuide, 'Instructions');

  /* Sheet 3 — the exact names the server knows. */
  // Names only — a code in brackets is accepted too, but the plain name is
  // what people should copy.
  const names = (list) => (list || []).map((x) => x.name);

  const lists = [
    ['Departments', names(lookups.departments)],
    ['Designations', names(lookups.designations)],
    ['Locations', names(lookups.locations)],
    ['Grades', names(lookups.grades)],
    ['Employment Categories', names(lookups.categories)],
    ['Shifts', names(lookups.shifts)],
    ['Provinces', names(lookups.provinces)],
    ['Districts', names(lookups.districts)],
    ['District belongs to Province', (lookups.districts || []).map((x) => x.parentName || '')],
    ['Electorates', names(lookups.electorates)],
    ['Electorate belongs to District', (lookups.electorates || []).map((x) => x.parentName || '')],
    ['Police Stations', names(lookups.policeStations)],
    ['Station belongs to Electorate', (lookups.policeStations || []).map((x) => x.parentName || '')],
    ['Banks (approved only)', names(lookups.banks)],
    ['Bank Branches', (lookups.branches || []).map((x) => x.name)],
    ['Branch belongs to Bank', (lookups.branches || []).map((x) => x.bankName || '')],
    ['Titles', TITLE_CHOICES],
    ['Genders', GENDER_CHOICES.map((c) => c.label)],
    ['Marital Statuses', MARITAL_CHOICES.map((c) => c.label)],
    ['Religions', RELIGION_CHOICES.map((c) => c.label)],
    ['Blood Groups', BLOOD_CHOICES.map((c) => c.label)],
    ['Account Types', ACCOUNT_TYPE_CHOICES],
    // Reporting Employee accepts the Staff ID, the name, or this exact
    // "Name (STAFFID)" form — the same label the Add form's dropdown shows.
    ['Reporting Employees', (lookups.employees || []).map((x) => (x.code ? `${x.name} (${x.code})` : x.name))],
  ];
  const height = Math.max(1, ...lists.map(([, v]) => v.length));
  const refRows = [lists.map(([h]) => h)];
  for (let i = 0; i < height; i += 1) {
    refRows.push(lists.map(([, values]) => values[i] ?? ''));
  }
  const wsRef = XLSX.utils.aoa_to_sheet(refRows);
  wsRef['!cols'] = lists.map(() => ({ wch: 26 }));
  XLSX.utils.book_append_sheet(wb, wsRef, 'Reference Lists');

  return wb;
}

export function downloadTemplate(lookups = {}, filename = 'Employee_Import_Template.xlsx') {
  XLSX.writeFile(buildTemplateWorkbook(lookups), filename);
}

/* ── Reading an uploaded file ────────────────────────────────────────────── */

/** Excel serial numbers and Date cells both become yyyy-mm-dd. */
function cellToIso(value) {
  if (value === null || value === undefined || value === '') return '';
  if (value instanceof Date) return toIso(value);
  if (typeof value === 'number') {
    const parsed = XLSX.SSF ? XLSX.SSF.parse_date_code(value) : null;
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
  }
  return toIso(value);
}


export async function readWorkbook(file) {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });

  const sheetName = wb.SheetNames.includes('Employees') ? 'Employees' : wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  if (!sheet) throw new Error('The file has no readable sheet.');

  const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, raw: false, defval: '' });
  if (!grid.length) throw new Error('The sheet is empty.');

  const headerRow = grid[0].map((h) => fmt(h).toLowerCase());
  const indexOf = {};
  COLUMNS.forEach((c) => {
    // The current header first, then any older spelling it replaced.
    const names = [c.header, ...(c.aliases || [])];
    let at = -1;
    for (const name of names) {
      at = headerRow.indexOf(name.toLowerCase());
      if (at !== -1) break;
    }
    indexOf[c.key] = at;
  });

  const missing = COLUMNS.filter((c) => c.required && indexOf[c.key] === -1).map((c) => c.header);
  if (missing.length) {
    throw new Error(`The file is missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. Download the template and use its header row.`);
  }

  const dateKeys = new Set([
    'dateOfBirth', 'dateOfJoining',
    'recruitmentDate', 'promotedDate', 'permanentDate', 'retirementDate', 'terminationDate',
  ]);

  return grid.slice(1).map((cells, i) => {
    const row = { _row: i + 2 };
    COLUMNS.forEach((c) => {
      const at = indexOf[c.key];
      const raw = at === -1 ? '' : cells[at];
      row[c.key] = dateKeys.has(c.key) ? cellToIso(raw) : fmt(raw);
    });
    return row;
  }).filter((row) => COLUMNS.some((c) => row[c.key] !== ''));   // drop blank rows
}


const norm = (v) => fmt(v).replace(/\s*\([^)]*\)\s*$/, '').replace(/\s+/g, ' ').trim().toLowerCase();

export const findByName = (list, name) => {
  const raw = fmt(name);
  if (!raw) return null;
  const v = norm(raw);
  const whole = raw.replace(/\s+/g, ' ').trim().toLowerCase();

  return (list || []).find((x) => {
    if (norm(x.name) === v) return true;
    if (x.code && norm(x.code) === v) return true;
    if (String(x.id).toLowerCase() === v) return true;
    // "Name (CODE)" exactly as the dropdown renders it.
    if (x.code && `${norm(x.name)} (${norm(x.code)})` === whole) return true;
    return false;
  }) || null;
};

/** A few real options, so the error says what WOULD work. */
const sampleOf = (list, limit = 4) => {
  const names = (list || []).map((x) => x.name).filter(Boolean);
  if (!names.length) return 'the list is empty — check that the master data loaded';
  const shown = names.slice(0, limit).join(', ');
  return names.length > limit ? `${shown}, …` : shown;
};

export function validateRow(row, lookups = {}, existingServiceNos = new Set(), existingTins = new Map()) {
  const errors = [];
  const v = { ...row };

  /* Text rules — the same functions the Add form uses. */
  v.serviceNo = sanitiseStaffId(row.serviceNo);
  const staffErr = checkStaffId(v.serviceNo);
  if (staffErr) errors.push(staffErr);
  else if (existingServiceNos.has(v.serviceNo.toLowerCase())) {
    errors.push(`Staff ID ${v.serviceNo} already exists`);
  }

  v.epfNo = sanitiseEpf(row.epfNo);
  const epfErr = checkEpf(v.epfNo);
  if (epfErr) errors.push(epfErr);


  v.tinNo = fmt(row.tinNo);
  if (v.tinNo && !/^\d+$/.test(v.tinNo)) {
    errors.push(`TIN No ${v.tinNo} must contain numbers only`);
  }
  const tinKey = normaliseTin(v.tinNo);
  if (tinKey && existingTins.has(tinKey)) {
    errors.push(`TIN No ${v.tinNo} is already used by ${existingTins.get(tinKey) || 'another employee'}`);
  }

  v.nicNo = normaliseNic(row.nicNo);
  const nicErr = checkNic(v.nicNo);
  if (nicErr) errors.push(nicErr);

  if (!fmt(row.firstName)) errors.push('First name is required');
  if (!fmt(row.lastName)) errors.push('Last name is required');
  if (!fmt(row.initials)) errors.push('Initials are required');

  const dobErr = checkDateOfBirth(row.dateOfBirth);
  if (dobErr) errors.push(dobErr);

  const dojErr = checkDateOfJoining(row.dateOfJoining);
  if (dojErr) errors.push(dojErr);

  // Mobile: required, exactly 10 numbers — the same rule as the Add/Edit forms.
  v.mobileNo = sanitisePhone(row.mobileNo);
  const mobileErr = checkMobile(v.mobileNo, 'Mobile no');
  if (mobileErr) errors.push(mobileErr);

  // Other numbers: optional, numbers only, max 10.
  ['homeNo', 'officeNo', 'altContactNumber'].forEach((key) => {
    v[key] = sanitisePhone(row[key]);
    const label = key === 'altContactNumber' ? 'Contact no' : 'Phone number';
    const err = checkPhone(v[key], label);
    if (err) errors.push(err);
  });

  // Emergency Contact: Yes / No (blank = No).
  const emergencyRaw = fmt(row.contactIsEmergency).toLowerCase();
  if (!emergencyRaw || ['no', 'n', 'false', '0'].includes(emergencyRaw)) {
    v.contactIsEmergency = false;
  } else if (['yes', 'y', 'true', '1'].includes(emergencyRaw)) {
    v.contactIsEmergency = true;
  } else {
    v.contactIsEmergency = false;
    errors.push(`Emergency Contact "${row.contactIsEmergency}" must be Yes or No`);
  }
  if (v.contactIsEmergency && !fmt(row.altContactName)) {
    errors.push('Emergency Contact is Yes but Contact Name is empty');
  }

  ['personalEmail', 'companyEmail'].forEach((key) => {
    const err = checkEmail(row[key], key === 'personalEmail' ? 'Personal email' : 'Company email');
    if (err) errors.push(err);
  });

  v.permanentAddressPostal = sanitisePostal(row.permanentAddressPostal);
  const postalErr = checkPostal(v.permanentAddressPostal);
  if (postalErr) errors.push(`Permanent postal code: ${postalErr}`);

  v.currentAddressPostal = sanitisePostal(row.currentAddressPostal);
  const currPostalErr = checkPostal(v.currentAddressPostal);
  if (currPostalErr) errors.push(`Current postal code: ${currPostalErr}`);

  const exp = parseExperience(row.totalExperience);
  const expErr = checkExperience(exp.years, exp.months);
  if (expErr) errors.push(expErr);
  else v.totalExperience = formatExperience(exp.years, exp.months);

  /* Choice columns — blank falls back to the form's default. */
  v.title = TITLE_CHOICES.find((t) => t.toLowerCase() === fmt(row.title).toLowerCase()) || '';
  if (fmt(row.title) && !v.title) errors.push(`Title "${row.title}" is not one of: ${TITLE_CHOICES.join(', ')}`);

  v.gender = codeFromChoices(row.gender, GENDER_CHOICES) || 'M';
  if (fmt(row.gender) && !codeFromChoices(row.gender, GENDER_CHOICES)) {
    errors.push(`Gender "${row.gender}" is not one of: ${choiceLabels(GENDER_CHOICES)}`);
  }

  v.maritalStatus = codeFromChoices(row.maritalStatus, MARITAL_CHOICES) || 'S';
  if (fmt(row.maritalStatus) && !codeFromChoices(row.maritalStatus, MARITAL_CHOICES)) {
    errors.push(`Marital status "${row.maritalStatus}" is not one of: ${choiceLabels(MARITAL_CHOICES)}`);
  }

  v.religion = codeFromChoices(row.religion, RELIGION_CHOICES);
  if (fmt(row.religion) && !v.religion) {
    errors.push(`Religion "${row.religion}" is not one of: ${choiceLabels(RELIGION_CHOICES)}`);
  }

  /* The raw cell is checked, not the sanitised one: sanitising strips letters,
     so "12a" would otherwise import silently as 12 instead of being queried. */
  v.distance = sanitiseDecimal(row.distance);
  const rawDistance = fmt(row.distance);
  if (rawDistance && !/^\d*\.?\d+$/.test(rawDistance)) {
    errors.push(`Distance "${rawDistance}" is not a number`);
  } else {
    const distErr = checkDecimal(v.distance, 'Distance');
    if (distErr) errors.push(distErr);
  }

  // An empty cell stays empty — it is not silently filled with a code.
  v.bloodGroup = codeFromChoices(row.bloodGroup, BLOOD_CHOICES) || '';
  if (fmt(row.bloodGroup) && !codeFromChoices(row.bloodGroup, BLOOD_CHOICES)) {
    errors.push(`Blood group "${row.bloodGroup}" is not one of: ${choiceLabels(BLOOD_CHOICES)}`);
  }

  v.accountType = ACCOUNT_TYPE_CHOICES.find(
    (t) => t.toLowerCase() === fmt(row.accountType).toLowerCase()
  ) || 'Savings';
  if (fmt(row.accountType) && !ACCOUNT_TYPE_CHOICES.some((t) => t.toLowerCase() === fmt(row.accountType).toLowerCase())) {
    errors.push(`Account type "${row.accountType}" is not one of: ${ACCOUNT_TYPE_CHOICES.join(', ')}`);
  }

  /* Name → id. An unknown name is an error, never a silent null. */
  const resolve = (name, listKey, label, required) => {
    const raw = fmt(name);
    if (!raw) {
      if (required) errors.push(`${label} is required`);
      return null;
    }
    const hit = findByName(lookups[listKey], raw);
    if (!hit) {
      errors.push(`${label} "${raw}" was not found. Valid values: ${sampleOf(lookups[listKey])}`);
      return null;
    }
    return hit.id;
  };

  v.designationId = resolve(row.designation, 'designations', 'Designation', true);
  // Required, as on the Add/Edit forms.
  v.departmentId  = resolve(row.department, 'departments', 'Department', true);
  v.locationId    = resolve(row.location, 'locations', 'Location', true);
  v.gradeId       = resolve(row.grade, 'grades', 'Grade', true);
  v.categoryId    = resolve(row.category, 'categories', 'Employment Category', true);

  /* Contract staff: Termination Date is mandatory and the four career dates
     are not used (sent blank). Every other category: no Termination Date. */
  const categoryHit = fmt(row.category) ? findByName(lookups.categories, row.category) : null;
  v.isContract = isContractCategory(categoryHit ? categoryHit.name : row.category);
  if (v.isContract) {
    v.recruitmentDate = '';
    v.promotedDate = '';
    v.permanentDate = '';
    v.retirementDate = '';
    v.terminationDate = fmt(row.terminationDate);
    if (!v.terminationDate) errors.push('Termination Date is required for Contract employees');
    else {
      const err = checkRowDate(v.terminationDate, 'Termination Date');
      if (err) errors.push(err);
    }
  } else {
    if (fmt(row.terminationDate)) {
      errors.push('Termination Date is only for Contract employees — leave it blank for this category');
    }
    v.terminationDate = '';
    [
      ['recruitmentDate', 'Recruitment Date', true],
      ['promotedDate', 'Promoted Date', false],
      ['permanentDate', 'Permanent Date', false],
      ['retirementDate', 'Retirement Date', false],
    ].forEach(([key, label, notFuture]) => {
      v[key] = fmt(row[key]);
      const err = checkRowDate(v[key], label, { notFuture });
      if (err) errors.push(err);
    });
  }
  v.shiftId       = resolve(row.shift, 'shifts', 'Shift', true);

  v.reportingEmployeeId = resolve(row.reportingEmployee, 'employees', 'Reporting Employee', false);

  v.provinceCode = null;
  v.districtCode = null;
  v.electorateCode = null;
  v.policeStationCode = null;

  const provinceRaw = fmt(row.province);
  const districtRaw = fmt(row.district);
  const electorateRaw = fmt(row.electorate);
  const stationRaw = fmt(row.policeStation);

  if (provinceRaw) {
    const hit = findByName(lookups.provinces, provinceRaw);
    if (!hit) errors.push(`Province "${provinceRaw}" was not found. Valid values: ${sampleOf(lookups.provinces)}`);
    else v.provinceCode = hit.id;
  }

  if (districtRaw) {
    if (!provinceRaw) errors.push('District needs a Province in the same row');
    const hit = findByName(lookups.districts, districtRaw);
    if (!hit) {
      errors.push(`District "${districtRaw}" was not found. Valid values: ${sampleOf(lookups.districts)}`);
    } else if (v.provinceCode && String(hit.parentId) !== String(v.provinceCode)) {
      errors.push(`District "${districtRaw}" is not in ${provinceRaw}`);
    } else {
      v.districtCode = hit.id;
    }
  }

  if (electorateRaw) {
    if (!districtRaw) errors.push('Electorate needs a District in the same row');
    const hit = findByName(lookups.electorates, electorateRaw);
    if (!hit) {
      errors.push(`Electorate "${electorateRaw}" was not found. Valid values: ${sampleOf(lookups.electorates)}`);
    } else if (v.districtCode && String(hit.parentId) !== String(v.districtCode)) {
      errors.push(`Electorate "${electorateRaw}" is not in ${districtRaw}`);
    } else {
      v.electorateCode = hit.id;
    }
  }

  if (stationRaw) {
    if (!electorateRaw) errors.push('Police Station needs an Electorate in the same row');
    const hit = findByName(lookups.policeStations, stationRaw);
    if (!hit) {
      errors.push(`Police Station "${stationRaw}" was not found. Valid values: ${sampleOf(lookups.policeStations)}`);
    } else if (v.electorateCode && String(hit.parentId) !== String(v.electorateCode)) {
      errors.push(`Police Station "${stationRaw}" is not in ${electorateRaw}`);
    } else {
      v.policeStationCode = hit.id;
    }
  }

  /* A region is stored on an address row, so a row that names one without
     any address has nowhere to put it. */
  if ((provinceRaw || districtRaw || electorateRaw || stationRaw) &&
      !fmt(row.permanentAddressLine1) && !fmt(row.permanentAddressCity) &&
      !fmt(row.currentAddressLine1) && !fmt(row.currentAddressCity)) {
    errors.push('Region needs an address: fill in a Permanent or Current address on this row');
  }

  v.group = fmt(row.group);

  /* Bank: branch has to belong to the chosen bank. */
  v.bankId = null;
  v.branchId = null;
  const bankRaw = fmt(row.bankName);
  const branchRaw = fmt(row.branchName);
  if (bankRaw || branchRaw || fmt(row.accountNumber)) {
    const bank = bankRaw ? findByName(lookups.banks, bankRaw) : null;
    if (bankRaw && !bank) errors.push(`Bank "${bankRaw}" was not found among approved banks. Valid values: ${sampleOf(lookups.banks)}`);
    v.bankId = bank ? bank.id : null;

    if (branchRaw) {
      const candidates = (lookups.branches || []).filter(
        (b) => findByName([b], branchRaw) !== null
      );
      const branch = bank
        ? candidates.find((b) => String(b.bankId) === String(bank.id))
        : candidates[0];
      if (!branch) {
        errors.push(
          candidates.length && bank
            ? `Branch "${branchRaw}" does not belong to ${bankRaw}`
            : `Branch "${branchRaw}" was not found among approved branches. Valid values: ${sampleOf(lookups.branches)}`
        );
      }
      v.branchId = branch ? branch.id : null;
    }

    if (fmt(row.accountNumber) && (!v.bankId || !v.branchId)) {
      errors.push('An account number needs both a Bank and a Bank Branch');
    }
  }

  return { values: v, errors };
}

export function findDuplicateTins(rows) {
  const seen = new Set();
  const dupes = new Set();
  rows.forEach((r) => {
    const tin = normaliseTin(r.tinNo);
    if (!tin) return;
    if (seen.has(tin)) dupes.add(tin);
    seen.add(tin);
  });
  return dupes;
}


/** An optional yyyy-mm-dd column: blank is fine, otherwise a real date. */
function checkRowDate(value, label, { notFuture = false } = {}) {
  const val = fmt(value);
  if (!val) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
  if (!d || d.getFullYear() !== Number(m[1]) || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[3])) {
    return `${label} "${val}" must be a valid date in yyyy-mm-dd format`;
  }
  if (notFuture) {
    const now = new Date();
    if (d > new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)) {
      return `${label} cannot be a future date`;
    }
  }
  return '';
}

export function findDuplicateServiceNos(rows) {
  const seen = new Map();
  const dupes = new Set();
  rows.forEach((r) => {
    const id = fmt(r.serviceNo).toLowerCase();
    if (!id) return;
    if (seen.has(id)) dupes.add(id);
    seen.set(id, true);
  });
  return dupes;
}


export function buildAddEmployeePayload(v, createdBy, newEmployeeIsApproved) {
  const reportName = fmt(v.reportName) ||
    [v.firstName, v.middleName, v.lastName].filter(Boolean).join(' ');

  return {
    ActionType: 1,
    p_hedServiceNo:            fmt(v.serviceNo),
    p_hedEpfNo:                fmt(v.epfNo),
    p_hedEtfNo:                fmt(v.epfNo),
    p_TinNo:                   fmt(v.tinNo),
    p_hedTitle:                fmt(v.title) || 'Mr.',
    p_hedFirstName:            fmt(v.firstName),
    p_hedMiddleName:           fmt(v.middleName),
    p_hedLastName:             fmt(v.lastName),
    p_hedInitials:             fmt(v.initials),
    p_hedReportName:           reportName,
    p_hedGender:               fmt(v.gender),
    p_hedDateOfBirth:          fmt(v.dateOfBirth),
    p_hedNicNo:                normaliseNic(v.nicNo),
    p_hedPassportNo:           fmt(v.passportNo),
    p_hedMaritalStatus:        fmt(v.maritalStatus),
    p_hedReligion:             fmt(v.religion),
    p_hedNationality:          '1',
    p_hedBloodGroup:           fmt(v.bloodGroup) || null,
    p_Distance:                fmt(v.distance),

    p_hedMobileNo:             fmt(v.mobileNo),
    p_hedHomeNo:               fmt(v.homeNo),
    p_hedOfficeNo:             fmt(v.officeNo),
    p_hedPersonalEmail:        fmt(v.personalEmail),
    p_hedCompanyEmail:         fmt(v.companyEmail),

    p_hedDepartmentId:         v.departmentId ?? null,
    p_hedLocationId:           v.locationId ?? null,
    p_hedDesignationId:        v.designationId ?? null,
    p_hedGradeId:              v.gradeId ?? null,
    p_hedEmploymentCategoryId: v.categoryId ?? null,
    p_hedShiftId:              v.shiftId ?? null,

    p_hedDateOfJoining:        fmt(v.dateOfJoining),
    // Blank for Contract (validateRow clears them); Termination only for Contract.
    p_RecruitmentDate:         fmt(v.recruitmentDate),
    p_PromotedDate:            fmt(v.promotedDate),
    p_PermanentDate:           fmt(v.permanentDate),
    p_RetirementDate:          fmt(v.retirementDate),
    p_termination_date:        v.isContract ? fmt(v.terminationDate) : '',
    p_hedConfirmationDate:     '',
    p_hedNoticePeriodDays:     v.noticePeriodDays ? Number(v.noticePeriodDays) : null,
    p_OT:                      v.otRate ? Number(v.otRate) : null,
    p_hedReportingEmployeeId:  v.reportingEmployeeId ? Number(v.reportingEmployeeId) : null,

    p_hedIsSystemUser:         false,
    p_hedCreatedBy:            createdBy,
    p_hedIsActive:             true,
    p_hedRemarks:              fmt(v.remarks),
    p_hedGroup:                fmt(v.group),
    p_hedTotalExperiance:      fmt(v.totalExperience),
    p_hedCostCenter:           fmt(v.costCenter),

    p_heaAddressType:          'P',
    p_heaAddressLine1:         fmt(v.permanentAddressLine1),
    p_heaAddressLine2:         fmt(v.permanentAddressLine2),
    p_heaAddressLine3:         fmt(v.permanentAddressLine3),
    p_heaCity:                 fmt(v.permanentAddressCity),
    // Region is NOT sent here — it belongs to an address row, so it goes with
    // the AddEmployeeAddress payload instead (see buildAddressPayloads).
    p_heaCountryId:            null,
    p_heaPostalCode:           fmt(v.permanentAddressPostal),

    p_heaIsPrimary:            true,
    p_heaCreatedBy:            createdBy,
    p_heaIsActive:             true,
    p_heaRemarks:              '',

    p_hebBankId:               v.bankId ?? null,
    p_hebBranchId:             v.branchId ?? null,
    p_hebAccountName:          fmt(v.initials),
    p_hebAccountNumber:        fmt(v.accountNumber),
    p_hebAccountType:          fmt(v.accountType) || 'Savings',
    p_hebIsPrimary:            true,
    p_hebCreatedBy:            createdBy,
    p_hebIsActive:             true,
    p_hebRemarks:              '',

    p_isApproved:              newEmployeeIsApproved,

    p_hecContactName:          fmt(v.altContactName) || 'N/A',
    p_hecContactNumber:        fmt(v.altContactNumber),
    p_hecRelationship:         fmt(v.altContactRelationship) || 'Other',
    p_hecContactType:          contactTypeFor(v.contactIsEmergency),
    p_hecAddress:              fmt(v.altContactAddress),
    p_hecCreatedBy:            createdBy,
    p_hecIsActive:             true,
    p_hecRemarks:              '',

    p_heqQualificationTypeId:  1,
    p_heqQualificationName:    '',
    p_heqFieldOfStudy:         '',
    p_heqInstitute:            '',
    p_heqCountry:              'Sri Lanka',
    p_heqStartDate:            null,
    p_heqCompletedDate:        null,
    p_heqResult:               '',
    p_heqGrade:                '',
    p_heqGPA:                  '',
    p_heqCertificateNo:        '',
    p_heqAttachment:           '',
    p_heqIsHighest:            true,
    p_heqCreatedBy:            createdBy,
    p_heqIsActive:             true,
    p_heqRemarks:              '',
  };
}

/** The employment-detail fix-up the Add form sends after creating the record. */
export function buildEmploymentFixUp(v) {
  const diff = { p_hedServiceNo: fmt(v.serviceNo) };
  if (v.departmentId) diff.p_hedDepartmentId = v.departmentId;
  if (v.categoryId)   diff.p_hedEmploymentCategoryId = v.categoryId;
  if (v.gradeId)      diff.p_hedGradeId = v.gradeId;
  if (v.locationId)   diff.p_hedLocationId = v.locationId;
  if (v.shiftId)      diff.p_hedShiftId = v.shiftId;
  return Object.keys(diff).length > 1 ? diff : null;
}


/** Which address row carries the region: the permanent one, else the current. */
const regionCarrierType = (v) =>
  (fmt(v.permanentAddressLine1) || fmt(v.permanentAddressCity)) ? 'P'
  : (fmt(v.currentAddressLine1) || fmt(v.currentAddressCity)) ? 'C'
  : null;

export function buildAddressPayload(v, employeeId, createdBy, type = 'P') {
  const prefix = type === 'C' ? 'currentAddress' : 'permanentAddress';
  const line1 = fmt(v[`${prefix}Line1`]);
  const city  = fmt(v[`${prefix}City`]);
  if (!line1 && !city) return null;

  // Region is an attribute of the address, and only one row carries it.
  const region = regionCarrierType(v) === type ? {
    p_heaProvinceId:     v.provinceCode ?? null,
    p_heaDistrictId:     v.districtCode ?? null,
    p_heaElectorateCode: v.electorateCode ?? null,
    // "Staion" is the backend's spelling — kept verbatim.
    p_heaStaionCode:     v.policeStationCode ?? null,
  } : {};

  return {
    p_hedEmployeeId:   employeeId,
    p_heaCreatedBy:    createdBy,
    p_isActive:        'A',
    p_remarks:         '',
    p_heaAddressType:  type,
    p_heaAddressLine1: line1,
    p_heaAddressLine2: fmt(v[`${prefix}Line2`]),
    p_heaAddressLine3: fmt(v[`${prefix}Line3`]),
    p_heaCity:         city,
    p_heaPostalCode:   fmt(v[`${prefix}Postal`]),
    ...region,
  };
}

/** Both addresses, blanks dropped — what the import loop iterates over. */
export function buildAddressPayloads(v, employeeId, createdBy) {
  return ['P', 'C']
    .map((type) => buildAddressPayload(v, employeeId, createdBy, type))
    .filter(Boolean);
}

export function buildBankPayload(v, employeeId, createdBy, newBankIsApproved) {
  if (!v.bankId || !v.branchId || !fmt(v.accountNumber)) return null;
  return {
    p_hebBankId:        Number(v.bankId),
    p_hebBranchId:      Number(v.branchId),
    p_hebAccountName:   fmt(v.initials),
    p_hebAccountNumber: fmt(v.accountNumber),
    p_hebAccountType:   fmt(v.accountType) || 'Savings',
    p_hebIsPrimary:     1,
    p_hebCreatedBy:     createdBy,
    p_hedEmployeeId:    Number(employeeId),
    p_isApproved:       newBankIsApproved,
  };
}

export function buildContactPayload(v, employeeId, createdBy) {
  if (!fmt(v.altContactName)) return null;
  return {
    p_hedEmployeeId:    employeeId,
    p_hecContactName:   fmt(v.altContactName),
    p_hecContactNumber: fmt(v.altContactNumber),
    p_hecRelationship:  fmt(v.altContactRelationship) || 'Other',
    p_hecContactType:   contactTypeFor(v.contactIsEmergency),
    p_hecAddress:       fmt(v.altContactAddress),
    p_hecCreatedBy:     createdBy,
    p_hecIsActive:      true,
    p_hecRemarks:       '',
  };
}